import React, { createContext, useContext, useEffect, ReactNode, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Language, getCanonicalPath, getLocalizedPath } from '@/utils/localizedRoutes';
import { baseTranslations } from '@/data/baseTranslations';

export type { Language } from '@/utils/localizedRoutes';

export const SUPPORTED_LANGUAGES: { code: Language; name: string; nativeName: string; flag: string }[] = [
  { code: 'sk', name: 'Slovak', nativeName: 'Slovenčina', flag: '🇸🇰' },
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська', flag: '🇺🇦' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
];

interface TranslationCache {
  [key: string]: string;
}

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, fallback?: string) => string;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Extract language from URL path
function extractLanguageFromPath(pathname: string): Language {
  const pathParts = pathname.split('/').filter(Boolean);
  const firstPart = pathParts[0];

  if (firstPart && ['en', 'de', 'uk', 'ru', 'es'].includes(firstPart)) {
    return firstPart as Language;
  }

  return 'sk';
}

function getTranslationCacheKey(lang: Language) {
  return `translations_cache_${lang}`;
}

function getInitialLanguage(): Language {
  if (typeof window !== 'undefined') return extractLanguageFromPath(window.location.pathname);
  return 'sk';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const initialLanguage = getInitialLanguage();

  const [language, setLanguageState] = useState<Language>(initialLanguage);
  const [translations, setTranslations] = useState<TranslationCache>(() => {
    // Slovak can render immediately from local source strings to avoid flicker.
    if (initialLanguage === 'sk') return baseTranslations as unknown as TranslationCache;

    // For other languages, try cache first (prevents flicker after the first visit).
    if (typeof window !== 'undefined') {
      try {
        const cached = window.localStorage.getItem(getTranslationCacheKey(initialLanguage));
        if (cached) return JSON.parse(cached) as TranslationCache;
      } catch {
        // ignore
      }
    }

    return {};
  });

  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Extract language from URL on mount and path changes
  useEffect(() => {
    const detectedLang = extractLanguageFromPath(location.pathname);
    if (detectedLang !== language) {
      setLanguageState(detectedLang);
    }
  }, [location.pathname]);

  // Load translations when language changes
  useEffect(() => {
    const cacheKey = getTranslationCacheKey(language);

    // Prime UI immediately to minimize visible text swaps.
    if (language === 'sk') {
      setTranslations(baseTranslations as unknown as TranslationCache);
    } else if (typeof window !== 'undefined') {
      try {
        const cached = window.localStorage.getItem(cacheKey);
        if (cached) setTranslations(JSON.parse(cached) as TranslationCache);
      } catch {
        // ignore
      }
    }

    let cancelled = false;

    const loadTranslations = async () => {
      setIsLoading(true);
      console.log('Loading translations for language:', language);

      try {
        // Supabase has a default 1000-row limit per request.
        // We must paginate to ensure we load all translations, otherwise newer keys can be missing
        // and the UI will fall back to Slovak fallback strings.
        const pageSize = 1000;
        let from = 0;
        const allRows: { key: string; value: string }[] = [];

        while (true) {
          const { data, error } = await supabase
            .from('translations')
            .select('key, value')
            .eq('language', language)
            .range(from, from + pageSize - 1);

          if (error) {
            console.error('Error loading translations:', error);
            throw error;
          }

          const rows = (data ?? []) as { key: string; value: string }[];
          allRows.push(...rows);

          if (rows.length < pageSize) break;
          from += pageSize;
        }

        console.log('Loaded translations count:', allRows.length);

        const dbCache: TranslationCache = {};
        allRows.forEach((item) => {
          dbCache[item.key] = item.value;
        });

        const merged: TranslationCache =
          language === 'sk'
            ? ({
                ...(baseTranslations as unknown as TranslationCache),
                ...dbCache,
              } as TranslationCache)
            : dbCache;

        if (!cancelled) {
          setTranslations(merged);
          if (typeof window !== 'undefined') {
            try {
              window.localStorage.setItem(cacheKey, JSON.stringify(merged));
            } catch {
              // ignore
            }
          }
        }
      } catch (error) {
        console.error('Error loading translations:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadTranslations();
    return () => {
      cancelled = true;
    };
  }, [language]);

  const setLanguage = async (newLang: Language) => {
    if (newLang === language) return;

    // Check if we're on a blog detail page - if so, we need to handle slug translation
    const blogMatch = location.pathname.match(/^(?:\/(en|de|uk|ru|es))?\/blog\/(.+)$/);
    
    if (blogMatch) {
      const currentSlug = blogMatch[2];
      
      // Try to find the localized slug for the target language from database
      try {
        // First try to find by localized_slug
        let postId: string | null = null;
        
        const { data: byLocalizedSlug } = await supabase
          .from("blog_post_translations")
          .select("post_id")
          .eq("localized_slug", currentSlug)
          .limit(1)
          .maybeSingle();
        
        if (byLocalizedSlug?.post_id) {
          postId = byLocalizedSlug.post_id;
        } else {
          // Try to find by main slug
          const { data: byMainSlug } = await supabase
            .from("blog_posts")
            .select("id")
            .eq("slug", currentSlug)
            .limit(1)
            .maybeSingle();
          
          if (byMainSlug?.id) {
            postId = byMainSlug.id;
          }
        }
        
        if (postId) {
          // Get the target language translation
          const { data: targetTranslation } = await supabase
            .from("blog_post_translations")
            .select("localized_slug")
            .eq("post_id", postId)
            .eq("language", newLang)
            .maybeSingle();
          
          if (targetTranslation?.localized_slug) {
            const blogBasePath = newLang === 'sk' ? '/blog' : `/${newLang}/blog`;
            setLanguageState(newLang);
            navigate(`${blogBasePath}/${targetTranslation.localized_slug}`);
            return;
          }
        }
      } catch (e) {
        console.error('Error fetching blog translation:', e);
      }
      
      // Fallback: just change language prefix, keep same slug
      const blogBasePath = newLang === 'sk' ? '/blog' : `/${newLang}/blog`;
      setLanguageState(newLang);
      navigate(`${blogBasePath}/${currentSlug}`);
      return;
    }

    // Check if we're on an apostila document detail page
    const apostilaMatch = location.pathname.match(/^(?:\/(en|de|uk|ru|es))?(\/sluzby\/apostila|\/services\/apostille|\/dienstleistungen\/apostille|\/posluhy\/apostyl|\/uslugi\/apostil|\/servicios\/apostilla)\/(.+)$/);
    
    if (apostilaMatch) {
      const currentSlug = apostilaMatch[3];
      
      try {
        // First try to find by localized_slug
        let documentTypeId: string | null = null;
        
        const { data: byLocalizedSlug } = await supabase
          .from("apostila_document_translations")
          .select("document_type_id")
          .eq("localized_slug", currentSlug)
          .limit(1)
          .maybeSingle();
        
        if (byLocalizedSlug?.document_type_id) {
          documentTypeId = byLocalizedSlug.document_type_id;
        } else {
          // Try to find by main slug
          const { data: byMainSlug } = await supabase
            .from("apostila_document_types")
            .select("id")
            .eq("slug", currentSlug)
            .limit(1)
            .maybeSingle();
          
          if (byMainSlug?.id) {
            documentTypeId = byMainSlug.id;
          }
        }
        
        if (documentTypeId) {
          // Get the target language translation
          const { data: targetTranslation } = await supabase
            .from("apostila_document_translations")
            .select("localized_slug")
            .eq("document_type_id", documentTypeId)
            .eq("language", newLang)
            .maybeSingle();
          
          if (targetTranslation?.localized_slug) {
            const apostilaBasePath = getLocalizedPath('/sluzby/apostila', newLang);
            setLanguageState(newLang);
            navigate(`${apostilaBasePath}/${targetTranslation.localized_slug}`);
            return;
          }
        }
      } catch (e) {
        console.error('Error fetching apostila document translation:', e);
      }
      
      // Fallback: just change language prefix and base path, keep same slug
      const apostilaBasePath = getLocalizedPath('/sluzby/apostila', newLang);
      setLanguageState(newLang);
      navigate(`${apostilaBasePath}/${currentSlug}`);
      return;
    }

    // Check if we're on a foreign country detail page
    const countryMatch = location.pathname.match(/^(?:\/(en|de|uk|ru|es))?(\/sluzby\/zahranicne-dokumenty|\/services\/foreign-documents|\/dienstleistungen\/auslaendische-dokumente|\/posluhy\/inozemni-dokumenty|\/uslugi\/inostrannye-dokumenty|\/servicios\/documentos-extranjeros)\/(.+)$/);
    
    if (countryMatch) {
      const currentSlug = countryMatch[3];
      
      try {
        // First try to find by localized_slug
        let countryId: string | null = null;
        
        const { data: byLocalizedSlug } = await supabase
          .from("apostila_foreign_country_translations")
          .select("country_id")
          .eq("localized_slug", currentSlug)
          .limit(1)
          .maybeSingle();
        
        if (byLocalizedSlug?.country_id) {
          countryId = byLocalizedSlug.country_id;
        } else {
          // Try to find by main slug
          const { data: byMainSlug } = await supabase
            .from("apostila_foreign_countries")
            .select("id")
            .eq("slug", currentSlug)
            .limit(1)
            .maybeSingle();
          
          if (byMainSlug?.id) {
            countryId = byMainSlug.id;
          }
        }
        
        if (countryId) {
          // Get the target language translation
          const { data: targetTranslation } = await supabase
            .from("apostila_foreign_country_translations")
            .select("localized_slug")
            .eq("country_id", countryId)
            .eq("language", newLang)
            .maybeSingle();
          
          if (targetTranslation?.localized_slug) {
            const countryBasePath = getLocalizedPath('/sluzby/zahranicne-dokumenty', newLang);
            setLanguageState(newLang);
            navigate(`${countryBasePath}/${targetTranslation.localized_slug}`);
            return;
          }
        }
      } catch (e) {
        console.error('Error fetching foreign country translation:', e);
      }
      
      // Fallback: just change language prefix and base path, keep same slug
      const countryBasePath = getLocalizedPath('/sluzby/zahranicne-dokumenty', newLang);
      setLanguageState(newLang);
      navigate(`${countryBasePath}/${currentSlug}`);
      return;
    }

    // Get the canonical (Slovak) path from current location
    const canonicalPath = getCanonicalPath(location.pathname, language);
    
    // Get the new localized path for the target language
    const newPath = getLocalizedPath(canonicalPath, newLang);
    
    setLanguageState(newLang);
    navigate(newPath);
  };

  const t = (key: string, fallback?: string): string => {
    return translations[key] || fallback || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, isLoading }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}